import { type AxiosResponse } from "axios";
import { z } from "zod";

import { type ApiResponse, type ObjectList } from "./_types";
import { api } from "./stackApi";

// Validation schema
export const mobileSippeerSchema = z.object({
  id: z.number().optional(),
  name: z
    .string()
    .trim()
    .min(1, { error: "The name is required." })
    .max(255, "Name cannot be longer than 40 characters.")
    .regex(/^[-'a-zA-Z0-9À-ÖØ-öø-ÿ\\ \\,.+_"–]*$/, {
      error: "Name can only contain alphanumeric characters, spaces, commas, dots, and dashes.",
    }),
  username: z.string(),
  exten: z.string(),
  email: z.union([z.email({ error: "Email address isn't a valid format." }), z.literal(""), z.null()]).optional(),
  overflow: z.union([
    z.null(),
    z.object({
      id: z.number().nullish(),
      type: z.literal("RuleExtension").nullish(),
      exten: z.string(),
    }),
    z.object({
      id: z.number().nullish(),
      type: z.literal("RuleSound").nullish(),
      sound: z.number(),
    }),
    z.object({
      id: z.number().nullish(),
      type: z.literal("RuleCustom").nullish(),
      custom: z.string(),
    }),
  ]),
  ringSeconds: z
    .number({ error: "Ring seconds must be a number." })
    .min(1, { error: "Ring seconds must be at least 1." })
    .max(120, { error: "Ring seconds must be at most 120." }),
  moh: z.string().nullable(),
  fullname: z.string().nullable(),
  postalcode: z.string().min(1, { error: "Postal code is required." }),
  fromdomain: z.string(),
  callRecording: z.enum(["OFF", "INCOMING", "OUTGOING", "ALL"], { error: "You must pick a call recording mode." }),
  blocklistId: z.number().nullable(),
  countryCallingCode: z.number().nullable(),
  mohId: z.number().nullable(),
  outboundCidNumber: z.string().nullable(),
  internalRingtoneId: z.number().nullable(),
  distinctiveRingtoneId: z.number().nullable(),
  insertedAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Types
export type MobileSippeer = z.infer<typeof mobileSippeerSchema>;
export type MobileSippeerListItem = Pick<MobileSippeer, "id" | "name" | "username" | "exten"> & {
  id: string;
};
type MobileSippeerList = ObjectList<MobileSippeerListItem, "sippeers">;

async function getAllMobileSippeers({ pbxId }: { pbxId: string }): Promise<AxiosResponse<MobileSippeerList>> {
  return await api().get<MobileSippeerList>(`${pbxId}/sippeers/mobile`);
}

async function getMobileSippeer({
  pbxId,
  mobileSippeerId,
}: {
  pbxId: string;
  mobileSippeerId: string;
}): Promise<AxiosResponse<MobileSippeer>> {
  const response = await api().get<ApiResponse<MobileSippeer, "sippeer">>(
    `${pbxId}/sippeers/mobile/${mobileSippeerId}`,
  );
  return { ...response, data: response.data.sippeer };
}

async function putMobileSippeer({
  pbxId,
  mobileSippeer,
}: {
  pbxId: string;
  mobileSippeer: MobileSippeer;
}): Promise<AxiosResponse<string>> {
  return await api().put(`${pbxId}/sippeers/mobile/${mobileSippeer.id}`, {
    sippeer: mobileSippeer,
  });
}

export const mobileSippeers = {
  getAll: getAllMobileSippeers,
  get: getMobileSippeer,
  put: putMobileSippeer,
};
